import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doctorQueueApi } from '@/api/doctor-queue-api';
import { playIncomingCallRingtone, stopIncomingCallRingtone } from '@/lib/sound';
import { Button } from '@medikei/ui';
import {
  PhoneCall,
  PhoneOff,
  User,
  AlertCircle,
  Clock,
  Sparkles,
  Zap,
} from 'lucide-react';

export interface IncomingInvitationData {
  invitationId: string;
  expressId: string;
  patientName?: string;
  specialtyName?: string;
  summaryAI?: {
    summary?: string;
    chiefComplaint?: string;
    symptomIntensity?: number;
    symptomDuration?: string;
    redFlags?: string[];
    riskClassification?: string;
  } | unknown;
  riskClassification?: string;
  expiresAt: string;
}

interface IncomingCallModalProps {
  invitation: IncomingInvitationData;
  onClose: () => void;
}

export function IncomingCallModal({ invitation, onClose }: IncomingCallModalProps) {
  const navigate = useNavigate();
  const [secondsRemaining, setSecondsRemaining] = useState(30);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  useEffect(() => {
    // Start audio ringtone
    playIncomingCallRingtone();

    const expiresTime = new Date(invitation.expiresAt).getTime();
    const calculateSeconds = () => {
      const diff = Math.max(0, Math.floor((expiresTime - Date.now()) / 1000));
      setSecondsRemaining(diff);
      if (diff <= 0) {
        stopIncomingCallRingtone();
        onClose();
      }
    };

    calculateSeconds();
    const interval = setInterval(calculateSeconds, 1000);

    return () => {
      clearInterval(interval);
      stopIncomingCallRingtone();
    };
  }, [invitation.expiresAt, onClose]);

  const handleAccept = async () => {
    setIsAccepting(true);
    stopIncomingCallRingtone();

    try {
      const res = await doctorQueueApi.acceptInvitation(invitation.expressId);
      if (res.success && res.sessionId) {
        onClose();
        navigate(`/medico/telemedicine/video/${res.sessionId}`);
      } else {
        onClose();
        if (res.message) {
          alert(res.message);
        }
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      onClose();
      alert('Erro ao aceitar chamada. Tente novamente.');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    stopIncomingCallRingtone();

    try {
      await doctorQueueApi.rejectInvitation(invitation.expressId);
    } catch (err) {
      console.error('Error rejecting invitation:', err);
    } finally {
      setIsRejecting(false);
      onClose();
    }
  };

  const summary = (invitation.summaryAI as {
    summary?: string;
    chiefComplaint?: string;
    symptomIntensity?: number;
    symptomDuration?: string;
    riskClassification?: string;
  }) || {};

  const isUrgent = invitation.riskClassification === 'URGENT' || summary?.riskClassification === 'URGENT';
  const progressPercent = Math.max(0, Math.min(100, (secondsRemaining / 30) * 100));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-card rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl border border-border animate-in zoom-in-95 duration-200">
        {/* Header Alert */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center animate-bounce">
                <Zap className="w-6 h-6 text-amber-300" />
              </div>
              <div>
                <span className="text-xs uppercase font-bold tracking-widest text-blue-200">Pronto Atendimento</span>
                <h2 className="text-xl font-black">Nova Chamada Expressa</h2>
              </div>
            </div>

            {/* Countdown Badge */}
            <div className="flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-full text-xs font-bold font-mono">
              <Clock className="w-3.5 h-3.5 text-amber-300" />
              <span>{secondsRemaining}s</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/20">
            <div
              className="h-full bg-amber-400 transition-all duration-1000 ease-linear"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Patient & Clinical Details */}
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between pb-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <User className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold text-foreground text-base">
                  {invitation.patientName || 'Paciente em Espera'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {invitation.specialtyName || 'Clínica Geral / Pronto Atendimento'}
                </p>
              </div>
            </div>

            {isUrgent && (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Urgente
              </span>
            )}
          </div>

          {/* AI Pre-triage Briefing */}
          <div className="bg-muted/50 border border-border rounded-2xl p-4 space-y-2.5 text-sm">
            <div className="flex items-center gap-1.5 text-xs font-bold text-primary">
              <Sparkles className="w-4 h-4 text-primary" />
              <span>Resumo da Triagem Clínica com IA</span>
            </div>

            <p className="text-xs text-foreground/80 leading-relaxed font-medium">
              {summary?.summary || summary?.chiefComplaint || 'Paciente aguarda atendimento com sintomas agudos.'}
            </p>

            {(summary?.symptomIntensity || summary?.symptomDuration) && (
              <div className="flex gap-4 pt-1 text-xs text-muted-foreground">
                {summary?.symptomIntensity && (
                  <span>
                    Intensidade: <strong>{summary.symptomIntensity}/10</strong>
                  </span>
                )}
                {summary?.symptomDuration && (
                  <span>
                    Duração: <strong>{summary.symptomDuration}</strong>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              onClick={handleReject}
              disabled={isRejecting || isAccepting}
              variant="outline"
              className="py-3.5 rounded-xl text-foreground border-border hover:bg-muted font-semibold gap-2"
            >
              <PhoneOff className="w-4 h-4 text-red-500" />
              Recusar
            </Button>

            <Button
              onClick={handleAccept}
              disabled={isAccepting || isRejecting}
              loading={isAccepting}
              className="py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2 shadow-lg shadow-emerald-600/20"
            >
              <PhoneCall className="w-4 h-4 text-white" />
              Atender Agora
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
